/**
 * Admin endpoints of the survey system.
 *
 * Mirrors `ADMIN · Surveys` in the frozen contract. Every function calls the BFF unless this tab
 * asked for the fixtures with `?surveyMock=` — see `Surveys.mock.ts`, which is off by default and
 * cannot be committed on.
 *
 * Two failures carry data the screen needs, so they get error classes of their own rather than a
 * message the UI would have to parse: `SurveyVersionLockedError` (`409` on saving the
 * questionnaire of a version that already has answers) and `SurveyMissingTranslationsError`
 * (`422` on approving while a language is missing). Both live in `Surveys.errors.ts`.
 *
 * The three read endpoints pass `?language=` explicitly rather than trusting the BFF's own
 * default: the panel is written in Portuguese, and a label resolved from the base column puts
 * English question and option text next to Portuguese chrome. The identifiers stay raw.
 *
 * `ADMIN_LABEL_LANGUAGE` is the single switch, and `buildQuery` drops an `undefined`, so setting
 * it to `null` stops the parameter being sent without touching any call site.
 *
 * @module Pages/BUs/Surveys
 */

import {
  ExitSurveyAlreadyExistsError,
  parseExitSurveyConflict,
  parseTriggerConflict,
  SurveyMissingTranslationsError,
  SurveySlugHeldByDeletedError,
  SurveyTriggerLockedError,
  SurveyVersionLockedError,
} from './Surveys.errors';
import {
  isSurveyMockEnabled,
  mockApproveSurvey,
  mockBumpSurveyVersion,
  mockCreateSurvey,
  mockDeleteSurvey,
  mockDeleteSurveyRespondent,
  mockDiscardSurveyDraft,
  mockResetSurvey,
  mockRestoreSurvey,
  mockExportSurveyCsv,
  mockGetSurvey,
  mockGetSurveyComments,
  mockGetSurveyResponses,
  mockGetSurveyResults,
  mockListSurveys,
  mockPurgeSurvey,
  mockRejectSurvey,
  mockSaveSurveyQuestions,
  mockSubmitSurvey,
  mockUpdateSurvey,
} from './Surveys.mock';
import type {
  SaveQuestionsPayload,
  Survey,
  SurveyCommentsPage,
  SurveyCommentsRequest,
  SurveyCreatePayload,
  SurveyCsvFile,
  SurveyDetail,
  SurveyErrorResponse,
  SurveyListRequest,
  SurveyListResponse,
  SurveyPurgeResult,
  SurveyResetResult,
  SurveyRespondentErasure,
  SurveyResponsesPage,
  SurveyResponsesRequest,
  SurveyResults,
  SurveyResultsCut,
  SurveyTranslation,
  SurveyUpdatePayload,
} from '../Types/Surveys.types';
import { ADMIN_LABEL_LANGUAGE } from '../Types/Surveys.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';
import { parseDeletedHolder } from '@/Shared/utils/deletedHolderConflict';

const SURVEYS_ENDPOINT = '/v1/surveys';

export {
  ExitSurveyAlreadyExistsError,
  SurveyMissingTranslationsError,
  SurveySlugHeldByDeletedError,
  SurveyTriggerLockedError,
  SurveyVersionLockedError,
};

/**
 * Turns a failed response into an `Error` carrying the BFF's own message.
 *
 * The panel shows that message in the toast, so a 400 naming the offending question reaches the
 * person who wrote it instead of a generic phrase.
 */
const throwFromResponse = async (
  response: Response,
  fallback: string,
  byStatus: Record<number, string> = {},
): Promise<never> => {
  const body: Partial<SurveyErrorResponse> & {
    responseCount?: number;
    currentTrigger?: string;
    requestedTrigger?: string;
    missingLanguages?: string[];
    conflictingSurveyId?: string;
    conflictingSurveySlug?: string;
    conflictingSurveyApprovalStatus?: string;
  } = await response.json().catch(() => ({ message: fallback }));

  /*
    R45 — the opt-in feature already has an exit questionnaire. Checked before the
    `responseCount` branch only because it is the more specific body; the two never overlap.
    Read from the fields, never from the message, so this cannot claim a conflict that is not one.
  */
  if (response.status === 409) {
    const exitConflict = parseExitSurveyConflict(body);

    if (exitConflict) throw exitConflict;
  }

  /*
    R44 — the trigger cannot change once the survey has answers. Checked BEFORE the version lock and
    not after: this body carries a `responseCount` too, so the order is what keeps a trigger refusal
    from opening the version dialog, which would offer a bump that changes nothing. It is recognised
    by `currentTrigger` + `requestedTrigger`, fields the version lock never sends.
  */
  if (response.status === 409) {
    const triggerConflict = parseTriggerConflict(body);

    if (triggerConflict) throw triggerConflict;
  }

  if (response.status === 409 && typeof body.responseCount === 'number') {
    throw new SurveyVersionLockedError(
      body.message ?? 'A pesquisa já tem respostas.',
      body.responseCount,
    );
  }

  if (response.status === 422 && Array.isArray(body.missingLanguages)) {
    throw new SurveyMissingTranslationsError(
      body.message ?? 'Faltam traduções.',
      body.missingLanguages,
    );
  }

  /*
    A `409` whose message names a DELETED survey is not the same failure as one naming a live
    survey: the first has a way out — restore that row — and the second does not. Parsing is what
    the contract allows (the conflict carries prose, not fields), and an unreadable message falls
    through to the plain error, which is what the screen showed before this existed.
  */
  if (response.status === 409) {
    const holder = parseDeletedHolder(body.message);

    if (holder?.kind === 'survey') {
      throw new SurveySlugHeldByDeletedError(
        body.message ?? 'O slug pertence a uma pesquisa excluída.',
        holder.id,
        holder.deletedAt,
      );
    }
  }

  if (byStatus[response.status]) throw new Error(byStatus[response.status]);

  throw new Error(body.message || fallback);
};

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

/**
 * Accepts both listing shapes.
 *
 * `GET /v1/surveys` answers a flat `{ data, total, page, limit, totalPages }`, unlike the opt-in
 * listing's `{ data, pagination }`. A bare array is accepted too, so the screen survives either.
 */
const normalizeList = (payload: unknown): SurveyListResponse => {
  if (Array.isArray(payload)) {
    const data = payload as Survey[];

    return {
      data,
      total: data.length,
      page: 1,
      limit: data.length,
      totalPages: data.length ? 1 : 0,
    };
  }

  const envelope = payload as Partial<SurveyListResponse> & {
    pagination?: { page: number; limit: number; total: number; totalPages: number };
  };
  const data = envelope.data ?? [];
  const pagination = envelope.pagination;

  return {
    data,
    total: envelope.total ?? pagination?.total ?? data.length,
    page: envelope.page ?? pagination?.page ?? 1,
    limit: envelope.limit ?? pagination?.limit ?? data.length,
    totalPages: envelope.totalPages ?? pagination?.totalPages ?? (data.length > 0 ? 1 : 0),
  };
};

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.append(key, String(value));
  });

  const query = search.toString();

  return query ? `?${query}` : '';
};

export const getSurveys = async (params: SurveyListRequest = {}): Promise<SurveyListResponse> => {
  if (isSurveyMockEnabled()) return mockListSurveys(params);

  const url = `${env.bffBaseURL}${SURVEYS_ENDPOINT}${buildQuery({ ...params })}`;

  const response = await authenticatedFetch(url, { method: 'GET', headers: jsonHeaders });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar as pesquisas', {
      403: 'Você não tem permissão para ver as pesquisas',
    });
  }

  return normalizeList(await response.json());
};

export const getSurveyById = async (id: string): Promise<SurveyDetail> => {
  if (isSurveyMockEnabled()) return mockGetSurvey(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}`, {
    method: 'GET',
    headers: jsonHeaders,
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar a pesquisa', {
      403: 'Você não tem permissão para ver esta pesquisa',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

export const createSurvey = async (payload: SurveyCreatePayload): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockCreateSurvey(payload);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível criar a pesquisa', {
      403: 'Você não tem permissão para criar pesquisas',
      409: 'Já existe uma pesquisa com este slug nesta BU',
    });
  }

  return response.json();
};

export const updateSurvey = async (id: string, payload: SurveyUpdatePayload): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockUpdateSurvey(id, payload);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível salvar a pesquisa', {
      403: 'Você não tem permissão para editar esta pesquisa',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

export const deleteSurvey = async (id: string): Promise<void> => {
  if (isSurveyMockEnabled()) return mockDeleteSurvey(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}`, {
    method: 'DELETE',
    headers: jsonHeaders,
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível excluir a pesquisa', {
      403: 'Você não tem permissão para excluir esta pesquisa',
      404: 'Pesquisa não encontrada',
    });
  }
};

/** The whole questionnaire, in one transaction. `409` means the version already has answers. */
/**
 * Undoes the soft delete — `PATCH /:id/restore`, behind `survey:update`.
 *
 * No new permission: whoever can delete can undo their own mistake. It answers with the whole
 * survey, in the listing's own shape, so the row can be put back without a refetch.
 *
 * **It comes back deactivated, and that is the contract, not an accident.** `isActive` is forced
 * to `false` while `approvalStatus` is preserved, because delivery needs active AND approved AND
 * not deleted — restoring alone must never put a modal in front of real people at the instant
 * somebody undoes a delete. Putting it back on the air is a separate, deliberate `PATCH /:id`, and
 * the screen has to say so or the person waits for a delivery that will not come.
 */
export const restoreSurvey = async (id: string): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockRestoreSurvey(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/restore`, {
    method: 'PATCH',
    headers: jsonHeaders,
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível restaurar a pesquisa', {
      403: 'Você não tem permissão para restaurar pesquisas',
      404: 'Pesquisa não encontrada',
      409: 'Esta pesquisa não está excluída — talvez alguém já a tenha restaurado.',
    });
  }

  return response.json();
};

export const saveSurveyQuestions = async (
  id: string,
  payload: SaveQuestionsPayload,
): Promise<SurveyDetail> => {
  if (isSurveyMockEnabled()) return mockSaveSurveyQuestions(id, payload);

  const response = await authenticatedFetch(
    `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/questions`,
    { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(payload) },
  );

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível salvar o questionário', {
      403: 'Você não tem permissão para editar esta pesquisa',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/** Title and description per language, replaced wholesale. */
export const saveSurveyTranslations = async (
  id: string,
  translations: SurveyTranslation[],
): Promise<SurveyDetail> => {
  if (isSurveyMockEnabled()) return mockGetSurvey(id);

  const response = await authenticatedFetch(
    `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/translations`,
    { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ translations }) },
  );

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível salvar as traduções', {
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

export const submitSurveyForApproval = async (id: string, notes?: string): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockSubmitSurvey(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/submit`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(notes ? { notes } : {}),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível enviar para aprovação', {
      403: 'Você não tem permissão para enviar pesquisas para aprovação',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

export const approveSurvey = async (id: string): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockApproveSurvey(id);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/approve`, {
    method: 'POST',
    headers: jsonHeaders,
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível aprovar a pesquisa', {
      403: 'Você não pode aprovar a sua própria pesquisa — peça a outra pessoa',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

export const rejectSurvey = async (id: string, reason: string): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockRejectSurvey(id, reason);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/reject`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível rejeitar a pesquisa', {
      403: 'Você não tem permissão para rejeitar pesquisas',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/**
 * Throws the open draft away, without passing through a reviewer (R41).
 *
 * Behind `survey:update`, not `survey:reject`, and that is the whole design: `reject` is the
 * verdict of whoever **reviewed** and writes `rejectedAt` + `rejectionReason`; this is the
 * **author** binning their own draft and writes no review stamp at all. Reading the row a month
 * later has to still tell "foi recusada" from "foi abandonada".
 *
 * Empty body. Answers the whole survey, in the shape of `GET /v1/surveys/:id`, where
 * `draftVersion: null` is the confirmation — which is why the caller can reseed the form from it
 * instead of refetching.
 *
 * The `409` is **not** overridden: the BFF's own message explains that a draft which was already
 * approved is no longer a draft but the delivered version, and names `rollback-version` as the way
 * to undo that. Writing a shorter sentence here would drop the only pointer the person has.
 */
export const discardSurveyDraft = async (id: string): Promise<SurveyDetail> => {
  if (isSurveyMockEnabled()) return mockDiscardSurveyDraft(id);

  const response = await authenticatedFetch(
    `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/discard-draft`,
    { method: 'POST', headers: jsonHeaders },
  );

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível descartar o rascunho', {
      403: 'Você não tem permissão para editar esta pesquisa',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/** Opens the next version. `reask` is the whole difference between a fix and a new ask (R3). */
export const bumpSurveyVersion = async (id: string, reask: boolean): Promise<Survey> => {
  if (isSurveyMockEnabled()) return mockBumpSurveyVersion(id, reask);

  const response = await authenticatedFetch(
    `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/bump-version`,
    { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ reask }) },
  );

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível abrir a nova versão', {
      409: 'Já existe uma versão em rascunho aberta nesta pesquisa',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/**
 * The aggregates of one survey, one version, and — optionally — one cut (R49).
 *
 * `cut` carries both params or neither: one without the other is a `400`, and the type is what
 * keeps a half cut from ever reaching the query string. The refusals the BFF can still raise (a
 * question this version does not have, a question that is not a choice, a value the question has
 * no option for) are passed through with **its own wording**: they name what does exist, which is
 * what makes them readable on the first try, and the screen builds its selector so that none of
 * them happens in normal use.
 */
export const getSurveyResults = async (
  id: string,
  version?: number,
  cut?: SurveyResultsCut,
): Promise<SurveyResults> => {
  if (isSurveyMockEnabled()) return mockGetSurveyResults(id, version, cut);

  const url = `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/results${buildQuery({ version, language: ADMIN_LABEL_LANGUAGE ?? undefined, ...cut })}`;

  const response = await authenticatedFetch(url, { method: 'GET', headers: jsonHeaders });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar os resultados', {
      403: 'Você não tem permissão para ver os resultados',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

export const getSurveyResponses = async (
  id: string,
  params: SurveyResponsesRequest = {},
): Promise<SurveyResponsesPage> => {
  if (isSurveyMockEnabled()) return mockGetSurveyResponses(id, params);

  const url = `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/responses${buildQuery({ ...params, language: ADMIN_LABEL_LANGUAGE ?? undefined })}`;

  const response = await authenticatedFetch(url, { method: 'GET', headers: jsonHeaders });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar as respostas', {
      403: 'Você não tem permissão para ver as respostas',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/**
 * Erase every answer of a survey, from everybody, across every version (R26).
 *
 * Behind `survey:purge`, and behind typing the slug: the BFF refuses with `400` when `confirmSlug`
 * does not match exactly, and deletes nothing. That is not a formality the screen can skip — there
 * is no undo and no copy of this data anywhere else in the service.
 *
 * The survey definition survives. Only the responses go, with their answers, selected options and
 * step timings following by cascade.
 */
export const purgeSurvey = async (id: string, confirmSlug: string): Promise<SurveyPurgeResult> => {
  if (isSurveyMockEnabled()) return mockPurgeSurvey(id, confirmSlug);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/purge`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ confirmSlug }),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível apagar as respostas', {
      400: 'O slug digitado não bate com o da pesquisa. Nada foi apagado.',
      403: 'Você não tem permissão para apagar respostas. É survey:purge, que só o super admin tem.',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/**
 * Put the survey back the way it was created (R42) — `POST /:id/reset`, with the slug typed back.
 *
 * **The damage is a superset of the purge's**: that one erases the answers, this erases the answers
 * **and** every version above the first, plus the open draft and the rewording records of R39. So
 * the guards are at least the purge's — `survey:purge`, `confirmSlug` compared with `===`, no trim
 * and no case folding.
 *
 * What survives is the part that is counter-intuitive and therefore the part the screen has to
 * repeat: the survey, the **v1 questionnaire** and its translations, the opt-in link, the trigger,
 * the window and the whole eligibility block — and the approval state, because resetting neither
 * demotes nor approves.
 *
 * **Neither `400` is overridden.** One says the slug did not match, the other that version 1 has no
 * questionnaire and resetting would destroy the only one the survey has; both say nothing was
 * deleted, and both are more useful than anything this layer could write instead.
 */
export const resetSurvey = async (id: string, confirmSlug: string): Promise<SurveyResetResult> => {
  if (isSurveyMockEnabled()) return mockResetSurvey(id, confirmSlug);

  const response = await authenticatedFetch(`${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/reset`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ confirmSlug }),
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível resetar a pesquisa', {
      403: 'Você não tem permissão para resetar pesquisas. É survey:purge, que só o super admin tem.',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/**
 * Erase one person's answers to one survey, across every version of it (R27).
 *
 * Behind `survey:purge` on the BFF, which only `super_admin` holds. It is not a tidy-up: the
 * response row **is** the record that the person already answered, so removing it makes them
 * eligible again and the survey opens for them on the next visit — the same shape as
 * `reset_viewer` on a tour or an announcement.
 *
 * The `404` gets a sentence of its own because it is the one failure that is not a fault: the row
 * was already gone, most likely erased by somebody else while this list sat open.
 */
export const deleteSurveyRespondent = async (
  id: string,
  platformUserId: number,
): Promise<SurveyRespondentErasure> => {
  if (isSurveyMockEnabled()) return mockDeleteSurveyRespondent(id, platformUserId);

  const response = await authenticatedFetch(
    `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/responses/${platformUserId}`,
    { method: 'DELETE', headers: jsonHeaders },
  );

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível apagar a resposta', {
      403: 'Você não tem permissão para apagar respostas. É survey:purge, que só o super admin tem.',
      404: `Não há resposta de ${platformUserId} nesta pesquisa. Ou a pessoa nunca respondeu, ou alguém já apagou — recarregue a lista.`,
    });
  }

  return response.json();
};

export const getSurveyComments = async (
  id: string,
  params: SurveyCommentsRequest = {},
): Promise<SurveyCommentsPage> => {
  if (isSurveyMockEnabled()) return mockGetSurveyComments(id, params);

  const url = `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/comments${buildQuery({ ...params, language: ADMIN_LABEL_LANGUAGE ?? undefined })}`;

  const response = await authenticatedFetch(url, { method: 'GET', headers: jsonHeaders });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível carregar os comentários', {
      403: 'Você não tem permissão para ver os comentários',
      404: 'Pesquisa não encontrada',
    });
  }

  return response.json();
};

/** `Content-Disposition` names the file; falling back to the slug keeps the download usable. */
const filenameFromResponse = (response: Response, fallback: string): string => {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);

  return match?.[1] ?? fallback;
};

/**
 * The CSV, as a blob the caller saves.
 *
 * Fetched rather than linked to because the request needs the session headers that
 * {@link authenticatedFetch} adds — a plain `<a href>` would arrive unauthenticated.
 */
export const exportSurveyCsv = async (
  id: string,
  slug: string,
  version?: number,
): Promise<SurveyCsvFile> => {
  if (isSurveyMockEnabled()) return mockExportSurveyCsv(id, slug, version);

  const url = `${env.bffBaseURL}${SURVEYS_ENDPOINT}/${id}/export${buildQuery({ version })}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: { Accept: 'text/csv' },
  });

  if (!response.ok) {
    await throwFromResponse(response, 'Não foi possível exportar o CSV', {
      403: 'Você não tem permissão para exportar os resultados',
      404: 'Pesquisa não encontrada',
    });
  }

  return {
    filename: filenameFromResponse(response, `${slug}.csv`),
    blob: await response.blob(),
  };
};
